import { memo } from "react";

import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { Heading } from "@components/Heading";
import { Image } from "@components/Image";
import { Text } from "@components/Text";
import { TruncatedText } from "@components/TruncatedText";
import { tw, type TTailwindString } from "classnames";

type Props = {
  bannerText: Maybe<string>;
  description: Maybe<string>;
  linkReadLess?: string;
  linkReadMore?: string;
  ourGiftForYou?: string;
  thumbnailUrl: Maybe<string>;
  title: Maybe<string>;
};

const styles = {
  base: tw(
    "relative",
    "box-border",
    "flex",
    "w-full",
    "items-center",
    "rounded",
    "border-2",
    "border-solid",
    "border-sage-700",
    "bg-background-secondary",
    "px-4",
    "py-5",
    "text-primary"
  ),
  bannerText: tw("leading-0"),
  textContainer: tw("flex-1"),
  title: tw("mb-1"),
  description: tw("mt-2", "text-bone-900"),
  badge: tw(
    "absolute",
    "left-1/2",
    "top-0",
    "flex",
    "h-3.5",
    "-translate-x-1/2",
    "-translate-y-1/2",
    "transform",
    "items-center",
    "justify-center",
    "rounded-xl",
    "bg-sage-700",
    "px-2",
    "text-bone-50",
    "md:h-4.5"
  ),
  thumbnailContainer: tw(
    "mr-4",
    "inline-flex",
    "max-w-[72px]" as TTailwindString,
    "items-center",
    "justify-center",
    "mg:max-w-[112px]" as TTailwindString
  ),
};

const GiftWithPurchaseViewBase = ({
  bannerText,
  description,
  linkReadLess,
  linkReadMore,
  ourGiftForYou,
  thumbnailUrl,
  title,
}: Props) => {
  return (
    <div className={styles.base}>
      <div className={styles.badge}>
        <Text
          as="title2"
          className={styles.bannerText}
          size={{ sm: "3xs", md: "2xs" }}
        >
          {bannerText ?? ourGiftForYou}
        </Text>
      </div>
      {thumbnailUrl && (
        <div className={styles.thumbnailContainer}>
          <Image height={112} src={thumbnailUrl} width={112} />
        </div>
      )}

      <div className={styles.textContainer}>
        {title && (
          <Heading as="h6" className={styles.title}>
            {title}
          </Heading>
        )}
        {description && (
          <TruncatedText
            className={styles.description}
            linkReadLess={linkReadLess}
            linkReadMore={linkReadMore}
          >
            {description}
          </TruncatedText>
        )}
      </div>
    </div>
  );
};

export const GiftWithPurchaseView = memo(GiftWithPurchaseViewBase);
